import React, { useMemo, useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  User, 
  Mail, 
  Building,
  Search,
  Filter,
  MoreVertical,
  Shield,
  Key,
  ArrowUpDown
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import { Label } from '../ui/label';
import { useNavigate } from 'react-router-dom';
import { workspaceAdminAPI } from '../../services/api';
import { useTableControls } from '../../hooks/useTableControls';
import { TableControls } from '../ui/table-controls';
import { useAuth } from '../../contexts/AuthContext';
import { EmptyState, StatSkeletonGrid, CardSkeleton } from '../common/States';
import { StatCard } from '../common/StatCard';

const UsersCRUD = () => {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [bulkDeleteInProgress, setBulkDeleteInProgress] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserIds, setSelectedUserIds] = useState(() => new Set());
  const [formData, setFormData] = useState({
    email: '',
    first_name: '',
    last_name: '',
    role: 'employee',
    tenant_id: '',
    password: '',
    is_active: true
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [usersResponse, tenantsResponse] = await Promise.all([
        workspaceAdminAPI.listUsers(),
        workspaceAdminAPI.listTenants()
      ]);
      setUsers(usersResponse.data);
      setTenants(tenantsResponse.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    try {
      await workspaceAdminAPI.createUser({
        email: formData.email,
        first_name: formData.first_name,
        last_name: formData.last_name,
        role: formData.role,
        tenant_id: formData.tenant_id ? parseInt(formData.tenant_id, 10) : null,
        password: formData.password,
        is_active: formData.is_active
      });
      setIsCreateDialogOpen(false);
      setFormData({
        email: '',
        first_name: '',
        last_name: '',
        role: 'employee',
        tenant_id: '',
        password: '',
        is_active: true
      });
      await loadData();
    } catch (error) {
      console.error('Failed to create user:', error);
    }
  };

  const handleDelete = async () => {
    try {
      await workspaceAdminAPI.deleteUser(selectedUser.id);
      setIsDeleteDialogOpen(false);
      setSelectedUser(null);
      setSelectedUserIds((prev) => {
        const next = new Set(prev);
        next.delete(selectedUser.id);
        return next;
      });
      await loadData();
    } catch (error) {
      console.error('Failed to delete user:', error);
    }
  };

  // A page rather than the dialog below: the identity fields and a password
  // change do not both fit a modal, and a password change deserves a section
  // of its own rather than a field somebody fills in while renaming someone.
  const handleEditClick = (user) => {
    navigate(`/superadmin/users/edit/${user.id}`);
  };

  const handleDeleteClick = (user) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  const handleResetPassword = async (user) => {
    try {
      await workspaceAdminAPI.resetUserPassword(user.id);
      alert('Password reset email sent successfully');
    } catch (error) {
      console.error('Failed to reset password:', error);
    }
  };

  const filteredUsers = useMemo(() => users.filter(user => {
    // Search filter
    const matchesSearch = 
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Role filter
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    
    // Status filter
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && user.is_active) ||
      (statusFilter === 'inactive' && !user.is_active);
    
    return matchesSearch && matchesRole && matchesStatus;
  }), [users, searchTerm, roleFilter, statusFilter]);

  const table = useTableControls(filteredUsers, {
    initialSortKey: 'email',
    initialSortDirection: 'asc',
  });

  const normalizedCurrentEmail = String(currentUser?.email || '').trim().toLowerCase();

  const isSelfUser = (user) => {
    if (!normalizedCurrentEmail) return false;
    return String(user?.email || '').trim().toLowerCase() === normalizedCurrentEmail;
  };

  const toggleUserSelection = (userId, checked) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(userId);
      } else {
        next.delete(userId);
      }
      return next;
    });
  };

  const selectablePageUsers = useMemo(
    () => (table.data || []).filter((row) => row && row.id != null && !isSelfUser(row)),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isSelfUser depends only on normalizedCurrentEmail, already listed
    [table.data, normalizedCurrentEmail]
  );

  const selectedPageCount = useMemo(
    () => selectablePageUsers.filter((row) => selectedUserIds.has(row.id)).length,
    [selectablePageUsers, selectedUserIds]
  );

  const selectablePageCount = selectablePageUsers.length;
  const pageSelectState =
    selectablePageCount === 0
      ? false
      : selectedPageCount === 0
        ? false
        : selectedPageCount === selectablePageCount
          ? true
          : 'indeterminate';

  const selectedUserCount = selectedUserIds.size;

  const setAllPageSelection = (checked) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (checked) {
        selectablePageUsers.forEach((row) => next.add(row.id));
        return next;
      }
      selectablePageUsers.forEach((row) => next.delete(row.id));
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedUserIds.size === 0) return;
    setBulkDeleteInProgress(true);
    const ids = Array.from(selectedUserIds);
    const failures = [];
    for (const userId of ids) {
      const userRow = users.find((row) => row.id === userId);
      if (userRow && isSelfUser(userRow)) {
        failures.push(userId);
        continue;
      }
      try {
        await workspaceAdminAPI.deleteUser(userId);
      } catch (error) {
        failures.push(userId);
        console.error('Failed to delete user:', error);
      }
    }
    setIsBulkDeleteDialogOpen(false);
    setSelectedUserIds(new Set(failures));
    setBulkDeleteInProgress(false);
    await loadData();
  };

  const renderSortableHeader = (label, key) => (
    <button
      type="button"
      className="inline-flex items-center gap-1 font-medium hover:text-primary"
      onClick={() => table.onSort(key)}
    >
      {label}
      <ArrowUpDown className="h-3.5 w-3.5" />
    </button>
  );

  const getTenantName = (tenantId) => {
    const tenant = tenants.find(t => t.id === tenantId);
    return tenant ? tenant.name : 'Unknown';
  };

  const getRoleBadgeVariant = (role) => {
    switch (role) {
      case 'superadmin': return 'default';
      case 'admin': return 'secondary';
      case 'manager': return 'outline';
      default: return 'secondary';
    }
  };

  const summary = useMemo(() => {
    const total = users.length;
    const active = users.filter((u) => u.is_active).length;
    const admins = users.filter((u) => u.role === 'admin' || u.role === 'superadmin').length;
    const managers = users.filter((u) => u.role === 'manager').length;
    return { total, active, admins, managers };
  }, [users]);

  if (loading) {
    return (
      <div className="space-y-6">
        <StatSkeletonGrid count={4} />
        <CardSkeleton rows={3} />
        <CardSkeleton rows={8} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold">User Management</h2>
          <p className="text-muted-foreground">Manage all users across all organizations</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="destructive"
            disabled={selectedUserCount === 0}
            onClick={() => setIsBulkDeleteDialogOpen(true)}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Delete Selected{selectedUserCount > 0 ? ` (${selectedUserCount})` : ''}
          </Button>
          <Button onClick={() => setIsCreateDialogOpen(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Add User
          </Button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard label="Total Users" value={summary.total} icon={User} tone="primary" />
        <StatCard label="Active" value={summary.active} icon={Shield} tone="success" />
        <StatCard label="Admins" value={summary.admins} icon={Key} tone="info" />
        <StatCard label="Managers" value={summary.managers} icon={Building} tone="purple" />
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Role Filter */}
            <div className="w-full sm:w-auto">
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="all">All Roles</option>
                <option value="superadmin">Superadmin</option>
                <option value="admin">Admin</option>
                <option value="manager">Manager</option>
                <option value="employee">Employee</option>
              </select>
            </div>
            
            {/* Status Filter */}
            <div className="w-full sm:w-auto">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="w-10 p-4">
                    <Checkbox
                      checked={pageSelectState}
                      onCheckedChange={(checked) => setAllPageSelection(Boolean(checked))}
                      aria-label="Select all users on this page"
                    />
                  </th>
                  <th className="text-left p-4 font-medium">{renderSortableHeader('User', 'first_name')}</th>
                  <th className="text-left p-4 font-medium">{renderSortableHeader('Email', 'email')}</th>
                  <th className="text-left p-4 font-medium">{renderSortableHeader('Role', 'role')}</th>
                  <th className="text-left p-4 font-medium">{renderSortableHeader('Organization', 'tenant_id')}</th>
                  <th className="text-left p-4 font-medium">{renderSortableHeader('Status', 'is_active')}</th>
                  <th className="text-left p-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {table.data.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="p-0">
                      <EmptyState
                        icon={User}
                        title="No users found"
                        description="Try adjusting your search criteria or filters."
                      />
                    </td>
                  </tr>
                ) : (
                  table.data.map((user) => (
                  <tr key={user.id} className="border-b card-interactive hover:bg-muted/50">
                    <td className="p-4">
                      <Checkbox
                        checked={selectedUserIds.has(user.id)}
                        disabled={isSelfUser(user)}
                        onCheckedChange={(checked) => toggleUserSelection(user.id, Boolean(checked))}
                        aria-label={`Select user ${user.email}`}
                      />
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center">
                          <User className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="font-medium">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            ID: {user.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-muted-foreground" />
                        {user.email}
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant={getRoleBadgeVariant(user.role)}>
                        {user.role}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2">
                        <Building className="h-4 w-4 text-muted-foreground" />
                        {getTenantName(user.tenant_id)}
                      </div>
                    </td>
                    <td className="p-4">
                      <Badge variant={user.is_active ? 'default' : 'secondary'}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </td>
                    <td className="p-4">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuLabel>Actions</DropdownMenuLabel>
                          <DropdownMenuItem onClick={() => handleEditClick(user)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleResetPassword(user)}>
                            <Key className="h-4 w-4 mr-2" />
                            Reset Password
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => handleDeleteClick(user)}
                            className="text-destructive"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
        {filteredUsers.length > 0 && (
          <TableControls
            totalRows={table.totalRows}
            page={table.page}
            totalPages={table.totalPages}
            pageSize={table.pageSize}
            pageSizes={table.pageSizes}
            onPageSizeChange={table.onPageSizeChange}
            onPrevious={table.goToPreviousPage}
            onNext={table.goToNextPage}
            exportConfig={{
              title: 'Users',
              fileName: `users-${new Date().toISOString().split('T')[0]}`,
              rows: filteredUsers,
              columns: [
                { label: 'User ID', accessor: (row) => row.id },
                { label: 'First Name', accessor: (row) => row.first_name || '' },
                { label: 'Last Name', accessor: (row) => row.last_name || '' },
                { label: 'Email', accessor: (row) => row.email || '' },
                { label: 'Role', accessor: (row) => row.role || '' },
                { label: 'Organization', accessor: (row) => getTenantName(row.tenant_id) },
                { label: 'Status', accessor: (row) => (row.is_active ? 'Active' : 'Inactive') },
              ],
            }}
          />
        )}
      </Card>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Add a new user to the system. They will receive a welcome email.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="first_name">First Name</Label>
                <Input
                  id="first_name"
                  value={formData.first_name}
                  onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="last_name">Last Name</Label>
                <Input
                  id="last_name"
                  value={formData.last_name}
                  onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <select
                  id="role"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                >
                  <option value="employee">Employee</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                  <option value="superadmin">Superadmin</option>
                </select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="tenant_id">Organization</Label>
                <select
                  id="tenant_id"
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                  value={formData.tenant_id}
                  onChange={(e) => setFormData({...formData, tenant_id: e.target.value})}
                >
                  <option value="">Select Organization</option>
                  {tenants.map(tenant => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreate}>Create User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the user "{selectedUser?.email}".
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={isBulkDeleteDialogOpen} onOpenChange={setIsBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete {selectedUserCount} user{selectedUserCount === 1 ? '' : 's'}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleteInProgress}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkDeleteInProgress || selectedUserCount === 0}
              className="bg-destructive text-destructive-foreground"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UsersCRUD;
